/**
 * buildModularPatientOverviewModel.js
 *
 * Bridges Adult Autism's subcollection-based patient data (slim header +
 * pathway form docs) into the patientDataModel shape that the existing Patient
 * Overview components expect (PatientInfoPanel, PatientHistoryPanel,
 * PatientScalesPanel, PreAppointmentDashboardWithData).
 *
 * Why this adapter exists:
 * - Autism patients store questionnaire answers in adult_autism/assessment_001/formInstances/{formKey}
 *   documents (pathway sub-subcollection), not in the monolithic patient.formData
 *   blob used by ADHD.
 * - Service-user forms (GenericFormPage) persist answers nested by question
 *   key: answers[questionKey][serviceUserVarName], optionally with
 *   { skipped: true } at the question level.
 * - The pre-appointment dashboard panels were built against ADHD's
 *   formData[formKey].formSubsections with answers on each subquestion; this
 *   module merges nested formInstance answers into that shape at read time.
 *
 * This is a read-only view transformation — nothing written back to Firestore.
 */

import { AUTISM_CORE_FORM_KEYS, isAutismHistoryForm } from '../../../../constants/adultAutismFormKeys';
import { CYP_ADHD_CLINICAL_FORM_TITLES } from '../../../../constants/cypAdhdFormKeys';
import { buildDobString } from '../../../../utils/dobDateUtils';
import { normalizeStoredProgressToPercentage } from '../../../../utils/formProgress';
import { buildExternalAnswerLookups, isSubquestionVisible } from '../../../../utils/formVisibility';
import { isScorableScale } from '../../../serviceUsers/3_forms/functions/ScalesFunctions';

export { AUTISM_CORE_FORM_KEYS };

const EMPTY_PROFORMA_STAGE = {
  status: 'notStarted',
  progress: 0,
  sections: {},
  docs_available: false,
};

/**
 * Convert GenericFormPage answer shapes into the formats FormDetailsPanel and
 * formDisplayUtils expect (plain strings, { value, otherValue }, or arrays).
 */
export function normalizeModularAnswerValue(rawAnswer, inputType) {
  if (rawAnswer === undefined || rawAnswer === null) return undefined;
  if (typeof rawAnswer === 'object' && rawAnswer.skipped === true) return rawAnswer;

  if (inputType === 'selectMultiple') {
    if (Array.isArray(rawAnswer)) {
      // Demo format: [{ value: 'Option A' }, { value: 'Option B' }]
      if (
        rawAnswer.length > 0
        && rawAnswer.every((item) => typeof item === 'object' && item !== null && 'value' in item)
        && !rawAnswer.some((item) => Array.isArray(item?.selectedOptions))
      ) {
        return rawAnswer.map((item) => {
          const otherValue = item.otherValue || item.otherText;
          return otherValue ? { value: item.value, otherValue } : { value: item.value };
        });
      }
      return rawAnswer;
    }

    const selectedOptions = rawAnswer?.selectedOptions;
    if (!Array.isArray(selectedOptions)) return rawAnswer;

    const otherTexts = rawAnswer.otherTexts || {};
    return selectedOptions.map((option) => {
      const otherValue = otherTexts[option];
      return otherValue ? { value: option, otherValue } : { value: option };
    });
  }

  if (inputType === 'selectOne') {
    if (typeof rawAnswer === 'string') return rawAnswer;
    if (rawAnswer?.selected !== undefined) {
      const { selected, otherText } = rawAnswer;
      return otherText ? { value: selected, otherValue: otherText } : selected;
    }
    // Demo-data generator uses { value: '...' } instead of { selected: '...' }
    if (rawAnswer?.value !== undefined && !Array.isArray(rawAnswer)) {
      const { value, otherValue, otherText } = rawAnswer;
      const other = otherValue || otherText;
      return other ? { value, otherValue: other } : value;
    }
  }

  if (inputType === 'date') {
    return normalizeDateAnswer(rawAnswer);
  }

  return rawAnswer;
}

/**
 * Normalise a date answer to the string the display layer can render.
 *
 * The live DateOfBirthPicker stores an ISO string ('2019-03-14'), which is
 * passed through untouched. Older demo data stored a parts object
 * ({ day: '14', month: '3', year: '2019' }); FormattedAnswer has no notion
 * of that shape and printed it as "[object Object]" in the source dialog.
 * Those documents still exist until the demo patients are recreated, so the
 * parts object is folded into the canonical ISO string here, using the same
 * validated builder the picker itself uses so the two can never disagree.
 *
 * A parts object that is not a real calendar date (or is missing a part)
 * falls back to joining whatever parts are present with '/', mirroring the
 * server-side prefill formatter, so the clinician at least sees the raw
 * entry rather than nothing.
 */
function normalizeDateAnswer(rawAnswer) {
  if (typeof rawAnswer !== 'object' || Array.isArray(rawAnswer)) return rawAnswer;

  const hasPart = ['day', 'month', 'year'].some((key) => key in rawAnswer);
  if (!hasPart) return rawAnswer;

  const { day, month, year } = rawAnswer;
  const iso = buildDobString(day, month, year);
  if (iso) return iso;

  const parts = [day, month, year].filter((part) => part !== undefined && part !== null && `${part}` !== '');
  return parts.length ? parts.map((part) => `${part}`).join('/') : undefined;
}

/**
 * Returns true when a normalized / raw answer counts as "filled in" for
 * progress and completion indicators.
 */
export function isModularAnswerFilled(rawAnswer) {
  if (rawAnswer === undefined || rawAnswer === null || rawAnswer === '') return false;
  if (typeof rawAnswer === 'object' && rawAnswer.skipped === true) return true;

  if (Array.isArray(rawAnswer)) return rawAnswer.length > 0;

  if (typeof rawAnswer === 'object') {
    if (Array.isArray(rawAnswer.selectedOptions)) return rawAnswer.selectedOptions.length > 0;
    if (rawAnswer.selected !== undefined && rawAnswer.selected !== '') return true;
    if (rawAnswer.value !== undefined && rawAnswer.value !== '') return true;
    return Object.keys(rawAnswer).length > 0;
  }

  return true;
}

/**
 * GenericFormPage stores answers nested by question key:
 *   { "1": { firstName: "Jane", ... }, "2": { skipped: true } }
 *
 * Demo-data constants (and any legacy flat writes) may instead store a map
 * keyed directly by serviceUserVarName:
 *   { firstName: "Jane", title: { value: "Mr." }, ... }
 *
 * Detect which shape we have and convert flat → nested when needed so
 * FormDetailsPanel and progress calculations can read the answers.
 *
 * The flat `_skip_<questionVarName>` marker is NOT folded in here: question
 * keys repeat across subsections ("1", "2" ... restart in each), so a
 * question-level flag keyed by question key alone would land on every
 * question sharing that key. Readers ask `isModularQuestionSkipped` instead,
 * which keys off the question's own var name.
 */
export function normalizeAnswersToNested(formSubsections, answers = {}) {
  if (!answers || typeof answers !== 'object' || Array.isArray(answers)) {
    return {};
  }

  const questionKeys = new Set();
  const varNameToQuestionKey = {};

  Object.values(formSubsections || {}).forEach((subsection) => {
    Object.entries(subsection?.questions || {}).forEach(([questionKey, question]) => {
      questionKeys.add(String(questionKey));
      Object.values(question.subquestions || {}).forEach((subquestion) => {
        const varName = subquestion?.serviceUserVarName;
        if (varName) {
          varNameToQuestionKey[varName] = String(questionKey);
        }
      });
    });
  });

  const topKeys = Object.keys(answers);
  if (topKeys.length === 0) return {};

  const looksNested = topKeys.some((key) => {
    if (!questionKeys.has(key)) return false;
    const val = answers[key];
    return val !== null && typeof val === 'object' && !Array.isArray(val);
  });

  if (looksNested) return answers;

  const nested = {};
  Object.entries(answers).forEach(([varName, value]) => {
    const questionKey = varNameToQuestionKey[varName];
    if (!questionKey) return;
    if (!nested[questionKey]) nested[questionKey] = {};
    nested[questionKey][varName] = value;
  });

  return nested;
}

/**
 * Prefix of the flat skip marker GenericFormPage writes when a respondent
 * presses a question's skip button ("I'm unable to provide this
 * information"): `answers['_skip_<questionVarName>'] = true`, where
 * `questionVarName` falls back to the question key when the definition has
 * none. Kept in step with isQuestionSkipped in GenericFormPage.js.
 */
const FLAT_SKIP_MARKER_PREFIX = '_skip_';

/**
 * Was this question skipped by the respondent, in either stored shape?
 *
 *  - Flat marker (what GenericFormPage writes today): a top-level
 *    `_skip_<questionVarName>` key on the raw answers map. The var name is
 *    unique within a form, so this is unambiguous even though question keys
 *    repeat across subsections.
 *  - Nested legacy/demo shape: `answers[questionKey] = { skipped: true }`.
 *
 * Before this helper the clinician side only knew the nested shape, so a
 * question a parent had skipped looked simply unanswered: the derived
 * progress under-counted it, FormDetailsPanel showed no "Patient clicked
 * ..." badge, and the Clinical Interview source dialog had no skip note.
 * Every reader in this module (and getHistorySourcePrefillInputs) now goes
 * through here so they cannot disagree.
 *
 * A marker is never cleared by GenericFormPage when the parent comes back
 * and answers, so a question can be both skipped and answered; callers
 * treat the two independently, as the parent's own answer summary does.
 *
 * @param {object} rawAnswers - formInstance.answers exactly as stored
 * @param {string|number} questionKey - The question's key within its subsection
 * @param {object} question - The question definition (for questionVarName)
 * @returns {boolean}
 */
export function isModularQuestionSkipped(rawAnswers, questionKey, question) {
  if (!rawAnswers || typeof rawAnswers !== 'object' || Array.isArray(rawAnswers)) return false;
  const questionVarName = question?.questionVarName || String(questionKey);
  if (rawAnswers[`${FLAT_SKIP_MARKER_PREFIX}${questionVarName}`]) return true;
  const nestedEntry = rawAnswers[questionKey];
  return Boolean(
    nestedEntry && typeof nestedEntry === 'object' && !Array.isArray(nestedEntry) && nestedEntry.skipped === true,
  );
}

/**
 * Deep-clone form subsections and attach answers from the nested answers map
 * (keyed by question number) onto each subquestion's `.answer` field so
 * FormDetailsPanel and PatientHistoryPanel can render them like ADHD formData.
 */
export function mergeAnswersIntoFormSubsections(formSubsections, nestedAnswers = {}) {
  if (!formSubsections) return {};

  const answersByQuestion = normalizeAnswersToNested(formSubsections, nestedAnswers);
  const merged = JSON.parse(JSON.stringify(formSubsections));

  Object.entries(merged).forEach(([, subsection]) => {
    Object.entries(subsection?.questions || {}).forEach(([questionKey, question]) => {
      const questionAnswers = answersByQuestion[questionKey];

      // The skip flag and any stored answers are independent: a legacy
      // `{ skipped: true }` entry carries no answers, while a flat skip marker
      // can sit beside answers the parent gave after skipping. Set the flag
      // and still attach whatever is there, so nothing a parent typed is
      // hidden from the clinician.
      if (isModularQuestionSkipped(nestedAnswers, questionKey, question)) {
        question.wasSkipped = true;
      }

      Object.entries(question.subquestions || {}).forEach(([, subquestion]) => {
        const varName = subquestion?.serviceUserVarName;
        if (!varName || !questionAnswers) return;

        if (!Object.prototype.hasOwnProperty.call(questionAnswers, varName)) return;

        const raw = questionAnswers[varName];
        subquestion.answer = normalizeModularAnswerValue(raw, subquestion.inputType);
      });
    });
  });

  return merged;
}

/**
 * Flatten one form instance's answers into a `serviceUserVarName -> raw
 * answer` map, the shape `isSubquestionVisible` reads gating answers from.
 *
 * Answers are stored nested by question key (or, for demo data, flat by
 * variable name); `normalizeAnswersToNested` folds both into the nested
 * shape first, and this walks the definition so every variable the form
 * knows about appears in the map, as `null` when nothing is stored. Values
 * are left raw: the visibility engine normalises every stored shape itself.
 *
 * @param {object} formSubsections - formDefinition.formSubsections
 * @param {object} nestedAnswers - formInstance.answers
 * @returns {object} Map of serviceUserVarName -> raw answer (or null)
 */
export function buildFlatAnswerLookupFromInstance(formSubsections, nestedAnswers = {}) {
  const answersByQuestion = normalizeAnswersToNested(formSubsections, nestedAnswers);
  const lookup = {};

  Object.values(formSubsections || {}).forEach((subsection) => {
    Object.entries(subsection?.questions || {}).forEach(([questionKey, question]) => {
      const questionAnswers = answersByQuestion[questionKey];
      Object.values(question?.subquestions || {}).forEach((subquestion) => {
        const varName = subquestion?.serviceUserVarName;
        if (!varName) return;
        const raw = questionAnswers ? questionAnswers[varName] : undefined;
        lookup[varName] = raw === undefined ? null : raw;
      });
    });
  });

  return lookup;
}

/**
 * Count the questions a form shows and how many of them have an answer.
 *
 * This is the clinician-side fallback for the progress GenericFormPage
 * stores on the instance, so it has to see the form the way the parent saw
 * it. A question the parent was never shown must not sit in the total as
 * "unanswered", or a finished form reads as incomplete on the overview.
 * Two kinds of gate decide that:
 *
 *  - Same-form `conditionsToShow` (a follow-up that only appears after a
 *    "Yes"). Only the subquestions that are visible given this form's own
 *    answers are consulted, so a stale answer left behind on a hidden
 *    follow-up no longer counts as the question being answered.
 *  - Cross-form `otherForm` gates (the CYP periods questions, hidden when the
 *    About form says the child is Male or Intersex). These are resolved from
 *    `externalLookups`, keyed by sibling form key; leave it empty and they
 *    fail open, so a caller without sibling data sees every question.
 *
 * A question whose subquestions are ALL hidden drops out of the total, the
 * same rule GenericFormPage's calculateProgress applies. A question with no
 * subquestions at all is kept (nothing can hide it), as before.
 *
 * "Answered" deliberately stays at "at least one visible subquestion is
 * filled in" (or the question was skipped). GenericFormPage is stricter for
 * gated questions (every visible follow-up must be filled), but this count
 * is only ever a floor under the stored value (see
 * buildFormDataEntryFromInstance), and the history panels treat
 * `answeredQuestions === 0` as "not started", so tightening it here would
 * risk showing a started form as untouched.
 *
 * @param {object} formSubsections - formDefinition.formSubsections
 * @param {object} nestedAnswers - formInstance.answers
 * @param {object} [externalLookups={}] - Map of sibling formKey -> flat answer lookup
 * @returns {{ total: number, answered: number }}
 */
export function countAnsweredQuestions(formSubsections, nestedAnswers = {}, externalLookups = {}) {
  const answersByQuestion = normalizeAnswersToNested(formSubsections, nestedAnswers);
  const answerLookup = buildFlatAnswerLookupFromInstance(formSubsections, nestedAnswers);
  let total = 0;
  let answered = 0;

  Object.values(formSubsections || {}).forEach((subsection) => {
    Object.entries(subsection?.questions || {}).forEach(([questionKey, question]) => {
      const subquestions = Object.values(question.subquestions || {});
      const visibleSubquestions = subquestions.filter(
        (sq) => isSubquestionVisible(sq, answerLookup, externalLookups),
      );

      // Every subquestion gated off: the parent never saw this question.
      if (subquestions.length > 0 && visibleSubquestions.length === 0) return;

      total += 1;

      // Skipped counts as answered, in either stored shape. Checked before
      // the answers lookup because a flat-skipped question usually has no
      // answer entry at all.
      if (isModularQuestionSkipped(nestedAnswers, questionKey, question)) {
        answered += 1;
        return;
      }

      const questionAnswers = answersByQuestion[questionKey];
      if (!questionAnswers) return;

      const hasAnswer = visibleSubquestions.some((sq) => {
        const raw = questionAnswers[sq.serviceUserVarName];
        return isModularAnswerFilled(raw);
      });

      if (hasAnswer) answered += 1;
    });
  });

  return { total, answered };
}

/**
 * Build the sibling-form lookups a form's `otherForm` gates read.
 *
 * Only the siblings the definition actually names are flattened (today: the
 * About form, for Their early life), so every other form costs nothing and
 * a sibling that has not been assigned is simply absent, which the engine
 * treats as fail-open.
 *
 * @param {object} formInstance - The instance whose gates are being resolved
 * @param {object} siblingInstances - Map of formKey -> formInstance for the same respondent
 * @returns {object} Map of sibling formKey -> flat answer lookup
 */
function buildExternalLookupsForInstance(formInstance, siblingInstances = {}) {
  return buildExternalAnswerLookups(
    formInstance?.formDefinition,
    siblingInstances,
    (sibling) => buildFlatAnswerLookupFromInstance(
      sibling?.formDefinition?.formSubsections || {},
      sibling?.answers || {},
    ),
  );
}

/**
 * Build the formData[formKey] entry from a single formInstance document.
 *
 * `siblingInstances` is the full formInstances map for the same respondent;
 * it is only read to resolve cross-form gates on this form (see
 * countAnsweredQuestions) and may be omitted by callers that have a single
 * instance in hand, in which case those gates fail open.
 */
export function buildFormDataEntryFromInstance(formInstance, siblingInstances = {}) {
  const subsections = formInstance?.formDefinition?.formSubsections || {};
  const nestedAnswers = formInstance?.answers || {};
  const externalLookups = buildExternalLookupsForInstance(formInstance, siblingInstances);
  const { total, answered } = countAnsweredQuestions(subsections, nestedAnswers, externalLookups);
  const storedProgress = normalizeStoredProgressToPercentage(formInstance?.progress);
  const derivedProgress = total > 0 ? Math.round((answered / total) * 100) : storedProgress;

  // The magnifying-glass dialog reads one field, formData[formKey].lastUpdated,
  // which is where Adult ADHD writes the time on every save. A modular form
  // (CYP history questionnaires, the SNAP-IV rating scales, and the autism
  // equivalents) records that same moment as updatedAt on the form instance
  // instead. Without copying it across, the dialog finds nothing and leaves
  // the "Last updated" line out for every one of those forms. A scale scored
  // before updatedAt was being written still has the time on its score, so
  // that is the fallback.
  const lastUpdated = formInstance.updatedAt || formInstance.scaleResults?.lastUpdated;

  return {
    formTitle: formInstance.formTitle,
    formNumber: formInstance.formNumber,
    formSubsections: mergeAnswersIntoFormSubsections(subsections, nestedAnswers),
    progress: {
      progressPercentage: Math.max(storedProgress, derivedProgress),
      answeredQuestions: answered,
      totalQuestions: total,
    },
    completedAt: formInstance.status === 'completed' ? formInstance.updatedAt : undefined,
    lastUpdated: lastUpdated || undefined,
    status: formInstance.status,
    // Pass through the computed rating-scale score (written by GenericFormPage on
    // completion) so PatientScalesPanel can display it. History forms have no
    // scaleResults and simply carry undefined here.
    scaleResults: formInstance.scaleResults || undefined,
  };
}

/**
 * Synthesise dataModel['1_preAppointmentInformation'] from assigned formInstances
 * so PatientHistoryPanel / PatientScalesPanel can list forms by domain.
 */
export function buildPreAppointmentInformationSection(formInstances = {}) {
  const historyDomains = {};
  const scaleDomains = {};
  let historyOrder = 1;
  let scaleOrder = 1;

  const sortedEntries = Object.entries(formInstances).sort(
    ([, a], [, b]) => (a?.formNumber || 0) - (b?.formNumber || 0),
  );

  sortedEntries.forEach(([formKey, formInstance]) => {
    const domain = {
      internalName: formInstance.formTitle || formKey,
      displayName: formInstance.formTitle || formKey,
      varName: formKey,
      suggestedOrder: isAutismHistoryForm(formKey) ? historyOrder++ : scaleOrder++,
    };

    if (isAutismHistoryForm(formKey)) {
      historyDomains[formKey] = domain;
    } else {
      scaleDomains[formKey] = domain;
    }
  });

  // Assigned formInstances mean the patient has a questionnaire battery even
  // before they start answering — show the pre-appointment panels immediately.
  const hasAssignedForms = sortedEntries.length > 0;

  return {
    status: hasAssignedForms ? 'withData' : 'noData',
    progress: hasAssignedForms ? 100 : 0,
    docs_available: false,
    sections: {
      1: {
        internalName: 'Patient History',
        displayName: 'Patient History',
        varName: 'patientHistory',
        domains: historyDomains,
      },
      2: {
        internalName: 'Patient Scales',
        displayName: 'Patient Scales',
        varName: 'patientScales',
        domains: scaleDomains,
      },
    },
  };
}

/**
 * Ensure proforma stage keys exist on the dataModel so module progress
 * calculations and PatientInfoPanel feature flags do not throw on access.
 */
function ensureProformaStages(existingDataModel = {}) {
  const stageKeys = [
    '2_clinicalInterview',
    '3_semiStructuredInterview',
    '4_diagnosticDecision',
    '5_postAssessmentDiscussion',
    '6_manageReports',
  ];

  const dataModel = { ...existingDataModel };
  stageKeys.forEach((key) => {
    if (!dataModel[key]) {
      dataModel[key] = { ...EMPTY_PROFORMA_STAGE };
    }
  });
  return dataModel;
}

/**
 * Top-level adapter: modular context state → patientDataModel view object.
 */
export function buildModularPatientOverviewModel({
  header,
  formInstances = {},
  assessmentStages = {},
}) {
  if (!header) return null;

  const formData = {};
  Object.entries(formInstances).forEach(([formKey, formInstance]) => {
    formData[formKey] = buildFormDataEntryFromInstance(formInstance, formInstances);
  });

  const dataModel = ensureProformaStages(header.dataModel || {});
  dataModel['1_preAppointmentInformation'] = buildPreAppointmentInformationSection(formInstances);

  return {
    ...header,
    patientId: header.patientId,
    assessmentModule: header.assessmentModule || 'adultAutism',
    formData,
    dataModel,
    informants: header.informants || {},
    assessmentStages,
  };
}

/**
 * List patient-history form keys in display order for Adult Autism patients.
 * Used when building views that only need history (not rating scales).
 */
export function listModularPatientHistoryForms(formInstances = {}) {
  return Object.entries(formInstances)
    .filter(([formKey]) => isAutismHistoryForm(formKey))
    .sort(([, a], [, b]) => (a?.formNumber || 0) - (b?.formNumber || 0))
    .map(([formKey, formInstance]) => ({
      key: formKey,
      title: formInstance.formTitle || formKey,
    }));
}

/**
 * Build the ADHD-shaped formData map from modular formInstance documents.
 * Shared by patient overview (header formInstances) and autism informant views.
 */
export function buildFormDataMapFromInstances(formInstances = {}) {
  const formData = {};
  Object.entries(formInstances).forEach(([formKey, formInstance]) => {
    // The whole map goes along so this form's cross-form gates can read its
    // siblings (the CYP periods questions read the About form).
    formData[formKey] = buildFormDataEntryFromInstance(formInstance, formInstances);
  });
  return formData;
}

/**
 * List informant history forms in stable display order.
 *
 * Module-agnostic: ordered by each instance's own formNumber (autism 1-9,
 * CYP 1-11) rather than a per-module key whitelist — the previous autism-only
 * whitelist silently listed ZERO forms for a CYP informant. Autism output is
 * unchanged (its instances are exactly the whitelisted keys, already in
 * formNumber order). Rating scales are excluded via the scoring registry
 * (isScorableScale) — they have their own panels (autism: none in the
 * modular tree; CYP: the two SNAP-IV 26s in the rating-scales block).
 */
export function listModularInformantHistoryForms(formInstances = {}) {
  return Object.entries(formInstances)
    .filter(([formKey]) => !isScorableScale(formKey))
    .sort(([, a], [, b]) => (a?.formNumber || 0) - (b?.formNumber || 0))
    .map(([formKey, formInstance]) => ({
      key: formKey,
      // Clinicians see simple clinical names without the child's name (CYP
      // keys only — no autism key appears in the map, and the stored
      // parent-facing personalised title stays the fallback for everything
      // else).
      title:
        CYP_ADHD_CLINICAL_FORM_TITLES[formKey]
        || formInstance.formTitle
        || formKey,
    }));
}
